import type { StyledKeyframes } from '@lemonade-hq/cdk';
import { Box, Flex, font, themedColor, useAnimateOnClick } from '@lemonade-hq/cdk';
import { Checkbox, MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { COLORS } from '@lemonade-hq/tokens';
import parse from 'html-react-parser';
import { rgba } from 'polished';
import React, { useEffect, useMemo, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { SectionParagraph, SectionTitle } from '../../Content';
import { Spiral } from './Spiral';

const bounceCard = keyframes`
  15% {
    transform: scale(0.93);
  }
  30% {
    transform: scale(1.05);
  }
  50% {
    box-shadow: 0px 0px 46px 0px ${rgba(COLORS.pink, 0.2)};
  }
`;

const Title = styled(SectionTitle)`
  ${font('main', { fontSize: '1.8rem', fontWeight: 700 })};
  padding: 0;
  ${MEDIA_QUERIES.tabletAndDesktop`
         ${font('main', { fontSize: '2.6rem', fontWeight: 700 })};
    `}
`;
const Subtitle = styled(SectionParagraph)`
  ${font('main', { fontSize: '1.6rem' })};
  white-space: normal;
  margin-top: 5px;
  display: none;
  ${MEDIA_QUERIES.tabletAndDesktop`
         display:block;
    `}
`;

const CardWrapper = styled(Flex)<{ readonly isDisabled?: boolean }>`
  position: relative;
  width: 100%;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px 32px 20px;
  margin: 0 auto;
  background: ${themedColor('white')};
  border-radius: 8px;
  color: ${themedColor('night')};
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${({ isDisabled }) => (isDisabled ? 'none' : 'auto')};
  transition: all ease 200ms;
  box-shadow: 0px 47px 46px -15px rgba(black, 0.16);

  &:hover {
    text-decoration: none;
    transform: scale(1.05);
  }
`;

const ProductImage = styled(Box)<{
  readonly cardImage: string;
  readonly title: string;
}>`
  width: 106px;
  height: 106px;
  transition: all ease 250;

  ${({ cardImage }) => `background: url(${cardImage}) center no-repeat;`}
  background-size: contain;
  transform: ${({ title }) => (title === 'Renters' ? 'translateY(1px)' : 'translateY(-0)')};

  ${MEDIA_QUERIES.tabletAndDesktop`
        width: 150px;
        height: 150px;

        ${({ cardImage }: { readonly cardImage: string }) => `background: url(${cardImage}) center no-repeat;`}
        background-size: auto;
        background-position: bottom;
    `}
`;

interface CardProps {
  readonly item: {
    readonly title: string;
    readonly subtitle: string;
    readonly activeImage: string;
    readonly defaultImage: string;
  };
  readonly onClick: () => void;
  readonly isActive: boolean;
  readonly isDisabled: boolean;
  readonly idx: number;
}

interface CardContentProps extends Pick<CardProps, 'isActive' | 'isDisabled'> {
  readonly title: string;
  readonly titleId: string;
  readonly subtitle: string;
}

const CardContent: React.FC<Omit<CardContentProps, 'isActive' | 'isDisabled'>> = React.memo(
  ({ title, titleId, subtitle }) => {
    return (
      <Flex alignItems="center" flexDirection="column" justifyContent="space-between">
        <Box>
          <Title id={titleId}>{parse(title)}</Title>
          <Subtitle>{parse(subtitle)}</Subtitle>
        </Box>
      </Flex>
    );
  },
);

CardContent.displayName = 'CardContent';

interface CardImageProps {
  readonly cardImage: string;
  readonly title: string;
}

const CardImage: React.FC<CardImageProps> = React.memo(({ cardImage, title }) => {
  return <ProductImage cardImage={cardImage} title={title} alt="" />;
});

CardImage.displayName = 'CardImage';

interface CardCheckBoxProps {
  readonly isActive: boolean;
  readonly isDisabled: boolean;
  readonly titleId: string;
}

const CardCheckBox: React.FC<CardCheckBoxProps> = React.memo(({ isActive, isDisabled, titleId }) => {
  return (
    <Box mt={'30px'} style={{ pointerEvents: isDisabled ? 'none' : 'auto' }}>
      <Checkbox aria-labelledby={titleId} disabled={isDisabled} onChange={() => null} value={isActive} />
    </Box>
  );
});

CardCheckBox.displayName = 'CardCheckBox';

export const Card: React.FC<CardProps> = ({
  item: { title, activeImage, defaultImage, subtitle },
  onClick,
  isActive,
  isDisabled,
  idx,
}) => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const titleId = useMemo(() => `build-by-you-card-title-${title.replaceAll(' ', '-')}-${idx}`, [idx, title]);
  const [showSpiral, setShowSpiral] = React.useState(false);
  const parent = useAnimateOnClick({
    speed: 1500,
    keyframes: bounceCard as StyledKeyframes,
  }) as React.Ref<HTMLDivElement>;

  useEffect(() => () => timeoutRef.current && clearTimeout(timeoutRef.current), []);

  const handleClick = (): void => {
    if (isActive) {
      onClick();
      return;
    }

    setShowSpiral(true);
    onClick();
    if (timeoutRef.current) return;

    timeoutRef.current = setTimeout(() => {
      setShowSpiral(false);
      timeoutRef.current = undefined;
    }, 1000);
  };

  const cardImage = useMemo(() => (isActive ? activeImage : defaultImage), [isActive, activeImage, defaultImage]);

  return (
    <CardWrapper isDisabled={isDisabled} onClick={handleClick} opacity={isDisabled ? 0.5 : 1} ref={parent}>
      <CardImage cardImage={cardImage} title={title} />
      <CardContent subtitle={subtitle} title={title} titleId={titleId} />
      <CardCheckBox isActive={isActive} isDisabled={isDisabled} titleId={titleId} />
      {showSpiral && <Spiral />}
    </CardWrapper>
  );
};
